import type { HubPermission, HubRole } from "../types/hub";

export const hubRoles: readonly HubRole[] = [
  "SUPERADMIN",
  "ADMIN",
  "OPERATIONS",
  "TECHNICAL",
  "SALES",
  "MARKETING",
  "FINANCE",
  "USER",
  "AGENT",
];

/** Explicit baseline for current authenticated operators. No business, execution or secrets grants. */
export const operatorReadPermissions: readonly HubPermission[] = [
  "dashboard.read",
  "system.read",
  "integrations.read",
  "agents.read",
  "skills.read",
];

export function legacyHubPermissions(
  roles: readonly string[],
): HubPermission[] {
  if (
    !roles.some((role) =>
      [
        "OWNER",
        "SECURITY_ADMIN",
        "ADMIN",
        "MANAGER",
        "AUDITOR",
        "USER",
        "MEMBER",
        "admin",
        "user",
        "member",
      ].includes(role),
    )
  )
    return [];
  return [
    ...operatorReadPermissions,
    ...(roles.some((role) =>
      ["OWNER", "SECURITY_ADMIN", "ADMIN", "admin"].includes(role),
    )
      ? ["users.read" as const, "audit.read" as const]
      : []),
  ];
}

export interface PermissionGrant {
  organizationId: string;
  permission: HubPermission;
}

export function resolveGrants(
  organizationId: string,
  grants: readonly PermissionGrant[],
): HubPermission[] {
  return [
    ...new Set(
      grants
        .filter(
          (grant) =>
            grant.organizationId === organizationId && organizationId !== "",
        )
        .map((grant) => grant.permission),
    ),
  ];
}

// ── New RBAC: permission sets per role ──────────────────────────────────────

/**
 * All module-level permissions (the full set).
 * Used as the SUPERADMIN default.
 */
const ALL_MODULE_PERMISSIONS: readonly HubPermission[] = [
  // Module generic
  "MODULE_VIEW", "MODULE_EDIT", "MODULE_EXECUTE", "MODULE_ADMIN",
  // Administration
  "ADMINISTRATION_VIEW", "ADMINISTRATION_EDIT",
  // CRM
  "CRM_VIEW", "CRM_EDIT",
  // Marketing
  "MARKETING_VIEW", "MARKETING_EDIT",
  // Webs
  "WEBS_VIEW", "WEBS_EDIT",
  // Automations
  "AUTOMATIONS_VIEW", "AUTOMATIONS_CREATE", "AUTOMATIONS_EXECUTE",
  // AI
  "AI_USE",
  // Hermes
  "HERMES_VIEW", "HERMES_USE", "HERMES_EXECUTE", "HERMES_ADMIN",
  // Agents
  "AGENTS_VIEW", "AGENTS_MANAGE",
  // Skills
  "SKILLS_VIEW", "SKILLS_MANAGE",
  // MCP
  "MCP_VIEW", "MCP_MANAGE",
  // Tools
  "TOOLS_USE",
  // System
  "SYSTEM_SETTINGS", "USER_MANAGEMENT", "SECURITY_ADMIN_PERM",
  "ACTIVITY_LOG_VIEW", "SETUP_CENTER_ACCESS",
  // Projects
  "PROJECT_VIEW", "PROJECT_MANAGE",
  // Employees (Holded import)
  "EMPLOYEES_VIEW", "EMPLOYEES_IMPORT", "EMPLOYEES_MANAGE",
];

/** ADMIN gets most permissions except the most sensitive ones. */
const ADMIN_MODULE_PERMISSIONS: readonly HubPermission[] = [
  // Module generic
  "MODULE_VIEW", "MODULE_EDIT", "MODULE_EXECUTE", "MODULE_ADMIN",
  // Administration
  "ADMINISTRATION_VIEW", "ADMINISTRATION_EDIT",
  // CRM
  "CRM_VIEW", "CRM_EDIT",
  // Marketing
  "MARKETING_VIEW", "MARKETING_EDIT",
  // Webs
  "WEBS_VIEW", "WEBS_EDIT",
  // Automations
  "AUTOMATIONS_VIEW", "AUTOMATIONS_CREATE", "AUTOMATIONS_EXECUTE",
  // AI
  "AI_USE",
  // Hermes
  "HERMES_VIEW", "HERMES_USE", "HERMES_EXECUTE", "HERMES_ADMIN",
  // Agents
  "AGENTS_VIEW", "AGENTS_MANAGE",
  // Skills
  "SKILLS_VIEW", "SKILLS_MANAGE",
  // MCP
  "MCP_VIEW", "MCP_MANAGE",
  // Tools
  "TOOLS_USE",
  // System — except SECURITY_ADMIN_PERM and SETUP_CENTER_ACCESS
  "SYSTEM_SETTINGS", "USER_MANAGEMENT",
  "ACTIVITY_LOG_VIEW",
  // Projects
  "PROJECT_VIEW", "PROJECT_MANAGE",
];

/** MEMBER gets basic module view + limited use permissions. */
const MEMBER_MODULE_PERMISSIONS: readonly HubPermission[] = [
  "MODULE_VIEW",
  "CRM_VIEW",
  "MARKETING_VIEW",
  "WEBS_VIEW",
  "AUTOMATIONS_VIEW",
  "AI_USE",
  "HERMES_VIEW",
  "AGENTS_VIEW",
  "SKILLS_VIEW",
  "MCP_VIEW",
  "TOOLS_USE",
  "ACTIVITY_LOG_VIEW",
  "PROJECT_VIEW",
];

/** MANAGER gets member permissions + edit on key modules. */
const MANAGER_MODULE_PERMISSIONS: readonly HubPermission[] = [
  ...MEMBER_MODULE_PERMISSIONS,
  "MODULE_EDIT",
  "CRM_EDIT",
  "MARKETING_EDIT",
  "WEBS_EDIT",
  "AUTOMATIONS_CREATE",
  "HERMES_USE",
  "SKILLS_MANAGE",
  "PROJECT_MANAGE",
];

/** AUDITOR gets read-only across modules. */
const AUDITOR_MODULE_PERMISSIONS: readonly HubPermission[] = [
  "MODULE_VIEW",
  "CRM_VIEW",
  "MARKETING_VIEW",
  "WEBS_VIEW",
  "AUTOMATIONS_VIEW",
  "HERMES_VIEW",
  "AGENTS_VIEW",
  "SKILLS_VIEW",
  "MCP_VIEW",
  "ACTIVITY_LOG_VIEW",
  "PROJECT_VIEW",
];

/** SECURITY_ADMIN gets security-relevant permissions. */
const SECURITY_ADMIN_MODULE_PERMISSIONS: readonly HubPermission[] = [
  ...ADMIN_MODULE_PERMISSIONS,
  "SECURITY_ADMIN_PERM",
  "SETUP_CENTER_ACCESS",
];

/**
 * Map of DB role strings to their default module-level permission sets.
 * This is the new RBAC layer — works alongside legacy HubPermissionGrant rows.
 */
const ROLE_MODULE_PERMISSIONS: Record<string, readonly HubPermission[]> = {
  OWNER: ALL_MODULE_PERMISSIONS,
  SECURITY_ADMIN: SECURITY_ADMIN_MODULE_PERMISSIONS,
  ADMIN: ADMIN_MODULE_PERMISSIONS,
  MANAGER: MANAGER_MODULE_PERMISSIONS,
  MEMBER: MEMBER_MODULE_PERMISSIONS,
  AUDITOR: AUDITOR_MODULE_PERMISSIONS,
  SERVICE_ACCOUNT: MEMBER_MODULE_PERMISSIONS,
};

/**
 * Get the default module-level permissions for a given DB role.
 * Returns an empty array for unknown roles.
 */
export function getPermissionsForRole(role: string): HubPermission[] {
  return [...(ROLE_MODULE_PERMISSIONS[role] ?? [])];
}

/**
 * Check whether a set of permissions includes a specific required permission.
 * SUPERADMIN (role === 'OWNER') always passes.
 *
 * NOTE: hub.ts also exports hasHubPermission(principal, permission) for the
 * HubPrincipal pattern. This version works on raw permission arrays and is
 * exported as hasPermissionInSet to avoid barrel collision.
 */
export function hasPermissionInSet(
  permissions: readonly HubPermission[],
  required: HubPermission,
): boolean {
  return permissions.includes(required);
}